<template>
  <div
    ref="message-thread"
    data-role="message-thread"
    :class="[
      {
        '--overlay': overlay,
        '--hide-messages': hideMessages,
        '--ios': isIos(),
        '--thread': isThread,
        '--comments': isCommentThread,
        '--feed': isFeed
      },
      'container message-thread p-relative'
    ]"
  >
    <feed-header
      v-if="isFeed"
      :subscriber-avatars="subscriberAvatars"
      :message-thread-id="messageThreadId"
      :member-count="memberCount"
      :total-participant-count="totalParticipantCount"
      @enabled="markThreadRead"
    />

    <div
      ref="message-thread-wrapper"
      class="message-thread__content-wrapper"
      @click="resetTimer"
    >
      <div v-if="isExpanded" class="message-thread__content-container">
        <template
          v-if="
            messages.length === 0 &&
              isCommentThread &&
              !isLoading &&
              !isBuilderContext()
          "
        >
          <message-thread-prompt
            padding-top="8vh"
            :heading="$t('components.messageThread.thread.noComment')"
            :description="
              $t('components.messageThread.thread.startConversation')
            "
          />
        </template>
        <template v-else-if="isLoading && messages.length === 0">
          <thread-placeholder :is-feed="isFeed" class="w-100 mb-2" />
        </template>
        <template v-else>
          <div
            class="message-thread__content p-relative"
            :ref="`thread_${messageThreadId}`"
          >
            <div v-if="isThread" class="thread__line-vert" />
            <div
              v-for="group in messages"
              :key="group[0].message_ts"
              class="message-thread__group"
            >
              <message
                v-for="(message, i) in group"
                :key="message.message_ts"
                :message="message"
                :message-thread-id="messageThreadId"
                :is-self="isSelf(message, user)"
                :is-comment-thread="isCommentThread"
                :is-feed="isFeed"
                :is-thread="isThread"
                is-same-as-prev
                :is-last="group.length - 1 === i"
                :is-first="i === 0"
                @openImage="openImage"
                @react="data => handleReact(data.action, data.message)"
                @reply="handleReplyMessage"
                @showActions="m => showActionPanelFor(m)"
                @showReactions="m => showReactionPanelFor(m)"
              />
            </div>
          </div>
          {{ /* Due to flex-direction this is on top for chat */ }}
          <template v-if="isCommentThread">
            <div ref="load-observer" style="min-height: 10px">
              <div class="pb-3 pt-1 px-3">
                <v-button
                  v-if="messageThread && messageThread.remaining_messages"
                  small
                  ghost
                  block
                  :is-loading="isLoadingMessages"
                  @click="loadMessages"
                >
                  {{ $t("components.messageThread.thread.loadMoreComment") }}
                </v-button>
              </div>
            </div>
          </template>
          <template v-else>
            <div ref="load-observer" style="min-height: 10px">
              <div class="my-5 pt-3 px-2">
                <v-button
                  v-if="messageThread"
                  small
                  ghost
                  block
                  :is-loading="isLoadingMessages"
                  @click="() => loadMessages()"
                >
                  <template v-if="!overlay">
                    {{
                      messageThread.remaining_messages
                        ? $t("components.messageThread.thread.loadMore")
                        : isFeed
                        ? $t("components.messageThread.thread.noMorePosts")
                        : $t("components.messageThread.thread.noMoreMessages")
                    }}
                  </template>
                </v-button>
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>

    <div
      v-if="collapsable"
      class="message-thread__actions d-flex justify-content-between w-100 pb-1"
    >
      <div>
        <div v-if="isExpanded" class="d-block">
          <v-button small ghost class="pl-1" @click="collapseThread">
            <v-icon icon="unfold_less" />
            {{ $t("components.messageThread.thread.hideComment") }}
          </v-button>
        </div>

        <div v-else-if="messageThread.message_count > 0">
          <v-button
            small
            ghost
            with-icon
            class="pl-0 py-0 d-flex align-items-center"
            @click="expandThread"
          >
            <subscriber-avatars
              :subscribers="participants.map(p => p.user)"
              x-small
            />
            <span class="ml-1">
              {{ messageThread.message_count }}
              {{
                pluralize(
                  $t("components.messageThread.thread.comment"),
                  messageThread.message_count
                )
              }}
            </span>
          </v-button>
        </div>
      </div>

      <overlay-pill
        v-if="!overlay && withParticipants && memberCount > 1"
        :is-loading="isSubscribingToThread"
        icon="people"
        class="px-1"
      >
        <div class="green-dot"></div>
        {{
          $t("components.messageThread.thread.countMemberHere", {
            memberCount: memberCount
          })
        }}
      </overlay-pill>
    </div>

    <div v-if="isFeed" class="d-flex feed__footer-wrapper">
      <new-post-button
        v-if="allowToPost"
        :key="`new-post-${messageThreadId}`"
        :message-thread-id="messageThreadId"
        @posted="handleScrollToPost"
      />
    </div>
    <div
      v-else
      data-role="thread-footer"
      class="message-thread__footer-wrapper container d-flex align-items-end w-100"
    >
      <thread-footer
        :thread-id="messageThreadId"
        :is-authenticated="isAuthenticated"
        :is-comment-thread="isCommentThread"
        :is-thread="isThread"
      >
        <message-compose
          :thread-id="messageThreadId"
          :placeholder="placeholder"
          :is-loading="isSubscribingToThread || (isLoading && !messages.length)"
          @resetTimer="resetTimer"
          @expandThread="expandThread"
          @focus="handleComposeFocus"
          @blur="handleComposeBlur"
          @click="() => $emit('click')"
          @submit="handleSubmit"
        />
      </thread-footer>

      <template v-if="isAuthenticated && withReactions">
        <div class="message-thread__context mr-2 d-flex flex-column">
          <reaction
            v-for="reaction in reactions"
            :key="reaction.message_ts"
            :ts="reaction.message_ts"
          />

          <overlay-pill
            v-if="overlay && withParticipants"
            :is-loading="isSubscribingToThread"
            icon="people"
            class="px-1 mb-2"
          >
            {{ memberCount }}
          </overlay-pill>
          <v-button round raised @click="react">
            <v-icon icon="favorite" />
          </v-button>
        </div>
      </template>
    </div>

    <div
      v-if="isCommentThread && !isThread"
      class="message-thread__footer-action"
    >
      <v-button small raised outlined @click="scrollToCompose">{{
        footerActionText
      }}</v-button>
    </div>

    <message-action-modal
      v-if="isPresent(actionPanelMessage)"
      :is-comment="isCommentThread"
      :is-feed="isFeed"
      :message="actionPanelMessage"
      :is-liked="isLikedMessage(actionPanelMessage)"
      :username="username(actionPanelMessage.user)"
      @view="handleImageView"
      @react="action => handleReact(action, actionPanelMessage)"
      @reply="() => handleReplyMessage(actionPanelMessage)"
      @delete="handleDeleteMessage"
      @close="() => (actionPanelMessage = null)"
      @pin="handlePinPost"
      @unpin="message => handlePinPost(message, false)"
    />

    <message-reaction-modal
      v-if="isPresent(reactionPanelMessage)"
      :ts="reactionPanelMessage.message_ts"
      :message-thread-id="messageThreadId"
      @react="action => handleReact(action, reactionPanelMessage)"
    />

    <template
      v-if="
        !_inactive && withNotifications && isPresent(messageThreadId) && !isFeed
      "
    >
      <portal classes="mb-1" selector="#page-toolbar-actions">
        <notification-button
          :key="messageThreadId"
          :message-thread-id="messageThreadId"
          @enabled="markThreadRead"
        />
      </portal>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex"
import throttle from "lodash/throttle"

import { WINDOW_EVENTS } from "@shared/admin-app-constants"

import { MESSAGE_TYPES } from "@/lib/pusher-utils"
import {
  MESSAGE_ORDER,
  isReactionType,
  isSelf,
  buildMessageId,
  buildMessageFromPusherData
} from "@/lib/messages-utils"
import Timer, { TIMER_EVENTS, COUNT_DIRECTIONS } from "@/lib/timer"
import { isIos, isPreviewContext } from "@/lib/is-platform"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { isPresent, pluralize, isBuilderContext } from "@/lib/utils"
import openImage, { generateImageKeyForMessage } from "@/lib/mk-lightbox"

import OverlayPill from "@/components/OverlayPill"
import Message from "@/components/message-thread/Message"
import Reaction from "@/components/message-thread/Reaction"
import ThreadFooter from "@/components/message-thread/Footer"
import MessageThreadPrompt from "@/components/message-thread/Prompt"
import MessageCompose from "@/components/message-thread/MessageCompose"
import ThreadPlaceholder from "@/components/message-thread/ThreadPlaceholder"

export default {
  components: {
    OverlayPill,
    Message,
    Reaction,
    ThreadFooter,
    MessageThreadPrompt,
    MessageCompose,
    ThreadPlaceholder,
    NewPostButton: () => import("@/components/message-thread/NewPostButton"),
    NotificationButton: () =>
      import("@/components/message-thread/NotificationButton"),
    MessageActionModal: () => import("@/components/modals/MessageActionModal"),
    MessageReactionModal: () =>
      import("@/components/modals/MessageReactionModal"),
    SubscriberAvatars: () => import("@/components/SubscriberAvatars"),
    FeedHeader: () => import("@/components/message-thread/FeedHeader")
  },

  props: {
    messageThreadId: { type: [Number, String], required: true },
    isCommentThread: { type: Boolean, default: false },
    isFeed: { type: Boolean, default: false },
    overlay: { type: Boolean, default: false },
    withReactions: { type: Boolean, default: false },
    withParticipants: { type: Boolean, default: false },
    withNotifications: { type: Boolean, default: true },
    placeholder: {
      type: String,
      default: function() {
        return this.$t(
          "components.messageThread.thread.writeSomethingPlaceHolder"
        )
      }
    },
    autoHide: { type: Boolean, default: false },
    collapsable: { type: Boolean, default: false }
  },

  computed: {
    ...mapState("analytics", ["sessionId"]),
    ...mapState("auth", ["user", "hasCheckedAuthentication"]),
    ...mapGetters("auth", ["isAuthenticated", "currentUser"]),
    ...mapGetters("notifications", [
      "shouldAskForPermission",
      "canAskForPermission"
    ]),
    ...mapGetters("messages", [
      "getMessages",
      "getMessageThread",
      "hasUnreadMessageThreads",
      "getParticipant"
    ]),

    messages() {
      return this.getMessages({
        threadId: this.messageThreadId,
        order: this.renderOrder,
        grouped: true
      })
    },

    // Chats are rendered in reverse order (with previous messages above), however
    // they still need to be fetched from the API in descending order (LATEST_FIRST)
    // Therefore we need to maintain these two order values
    renderOrder() {
      return !this.isCommentThread &&
        !this.isFeed &&
        this.messageOrder === MESSAGE_ORDER.LATEST_FIRST
        ? MESSAGE_ORDER.OLDEST_FIRST
        : this.messageOrder
    },

    messageOrder() {
      return this.isCommentThread && this.isThread
        ? MESSAGE_ORDER.OLDEST_FIRST
        : MESSAGE_ORDER.LATEST_FIRST
    },

    isThread() {
      return !!this.messageThread.parent_message
    },

    allowToPost() {
      return (
        this.isFeed &&
        (!!this.messageThread?.allow_members_to_post ||
          !!this.currentUser?.isAccountAdmin)
      )
    },

    isPrivate() {
      return !!this.messageThread.is_private
    },

    messageThread() {
      return this.getMessageThread(this.messageThreadId) || {}
    },

    currentParticipant() {
      return this.getParticipant(this.messageThreadId)
    },

    participants() {
      return this.messageThread?.participants || []
    },

    totalParticipantCount() {
      return this.messageThread?.total_count_participants || 0
    },

    footerActionText() {
      return this.$t("components.messageThread.thread.addComment")
    },

    threadType() {
      return this.isCommentThread
        ? this.$t("components.messageThread.thread.comment")
        : this.isFeed
        ? this.$t("components.messageThread.thread.post")
        : this.$t("components.messageThread.thread.message")
    },

    subscriberAvatars() {
      return this.participants.length > 0
        ? this.participants.map(p => p.user)
        : []
    }
  },

  data() {
    return {
      memberIds: [],
      memberCount: 0,
      reactions: [],
      isInitialized: false,
      isLoading: true,
      isSubscribingToThread: false,
      isLoadingMessages: false,
      isExpanded: !this.collapsable,
      lastMessageFetched: null,
      countdownTimer: null,
      hideMessages: false,
      loadObserver: null,
      actionPanelMessage: null,
      reactionPanelMessage: null,
      memberParticipants: []
    }
  },

  methods: {
    isIos,
    isPresent,
    pluralize,
    openImage,
    isBuilderContext,
    isSelf,

    ...mapActions("messages", [
      "fetchMessageThread",
      "fetchMessages",
      "toggleNotification",
      "markRead",
      "subscribeToThread",
      "unsubscribeFromThread",
      "publishToThread",
      "deleteMessage",
      "reactMessage",
      "removeMessageThread",
      "pinPost",
      "addParticipantToThread"
    ]),

    showActionPanelFor(message) {
      if (!this.allowToAction(message)) return

      this.actionPanelMessage = message
      this.$router.push("#message-actions")
    },

    showReactionPanelFor(message) {
      this.reactionPanelMessage = message
      this.$router.push("#message-reactions")
    },

    allowToAction(message) {
      return !this.isFeed || message.is_root
    },

    handleImageView() {
      openImage({
        imageId: generateImageKeyForMessage(this.actionPanelMessage.message_ts)
      })
    },

    isLikedMessage(message) {
      return !!(this.currentParticipant.liked_messages || []).find(
        message_ts => message_ts === message.message_ts
      )
    },

    handleSubmit(messages = []) {
      messages.forEach(message => {
        this.publishMessage({
          user: this.user,
          ...message
        })
      })

      this.expandThread()
      this.markThreadRead()
      this.scrollToLatestMessage()
    },

    publishMessage(message) {
      if (!Object.values(MESSAGE_TYPES).includes(message.messageType)) return

      const createdAt = message.messageCreatedAt
        ? message.messageCreatedAt
        : +new Date()

      this.resetTimer()

      const messageBody = message.messageBody && message.messageBody.trim()

      let messageData = {
        messageBody: messageBody,
        messageCreatedAt: createdAt,
        user: message.user,
        messageType: message.messageType,
        memberIds: this.memberIds,
        messageThreadId: this.messageThreadId,
        sessionId: this.sessionId,
        isRoot: true,
        mentionedMembers: message.mentionedMembers || []
      }

      if (message.messageType === MESSAGE_TYPES.IMAGE) {
        messageData = {
          ...messageData,
          ...{ imageIds: message.imageIds, imageUrls: message.imageUrls }
        }
      } else if (message.messageType == MESSAGE_TYPES.VIDEO) {
        messageData = {
          ...messageData,
          ...{ videoUrl: message.videoUrl }
        }
      } else if (message.messageType == MESSAGE_TYPES.ATTACHMENT) {
        messageData = {
          ...messageData,
          ...{ attachmentData: message.attachmentData }
        }
      }

      this.publishToThread({
        messageThreadId: this.messageThreadId,
        message: messageData
      })

      if (!this.currentParticipant.has_contributed && this.withNotifications) {
        this.toggleNotification({
          messageThreadId: this.messageThreadId,
          active: true,
          toggleTs: createdAt - 1
        })
      }

      if (isReactionType(messageData)) {
        this.reactions.push(
          buildMessageFromPusherData(this.messageThreadId, messageData)
        )
      }

      if (this.shouldAskForPermission) {
        setTimeout(() => {
          EventBus.$emit(EVENTS.PROMPT_PUSH_PERMISSION)
        }, 600)
      } else if (
        this.canAskForPermission &&
        this.messages.flat().length === 1
      ) {
        setTimeout(() => {
          EventBus.$emit(EVENTS.PROMPT_PUSH_PERMISSION, {
            ignoreDismissed: true
          })
        }, 600)
      }
    },

    handleReact(action, message) {
      if (!this.isAuthenticated || !this.allowToAction(message)) return

      this.reactMessage({
        message,
        action,
        likedUserId: this.currentUser?.id,
        sessionId: this.sessionId
      })
    },

    handleReplyMessage(message) {
      if (!this.isAuthenticated) {
        this.$router.push("#auth")
        return
      }

      if (!this.allowToAction(message)) return

      const path = message.reply_thread_id
        ? `/me/messages/${message.reply_thread_id}`
        : `/me/messages/new?message=${buildMessageId(message)}`

      this.$router.removeHash().then(() => {
        this.$router.push(path)
      })
    },

    handleDeleteMessage(message) {
      if (message.messageType !== MESSAGE_TYPES.MESSAGE_DELETED) return

      this.deleteMessage({
        message: buildMessageFromPusherData(this.messageThreadId, message),
        sessionId: this.sessionId
      })

      this.$router.removeHash()
      this.$notify(`${this.threadType} Deleted`)
    },

    handlePinPost(message, isPinned = true) {
      this.pinPost({
        messageThreadId: message.message_thread_id,
        messageTs: message.message_ts,
        isPinned
      }).then(() => {
        setTimeout(() => {
          this.handleScrollToPost(message.message_ts)
        }, 400)

        if (isPinned) {
          this.$notify("Post Pinned")
        } else {
          this.$notify("Post Unpinned")
        }
      })

      this.$router.removeHash()
    },

    handleScrollToPost(ts) {
      this.$nextTick(() => {
        const messageEl = document.querySelector(`[data-ts="${ts}"]`)

        if (messageEl) {
          this.scrollToMessage(ts)
        } else {
          this.scrollToLatestMessage()
        }
      })
    },

    markThreadRead() {
      if (this._inactive) return

      this.markRead({
        messageThreadId: this.messageThreadId,
        clientTs: +new Date()
      })
    },

    handleMessageEvent(message) {
      if (isReactionType(message)) {
        this.reactions.push(
          buildMessageFromPusherData(this.messageThreadId, message)
        )
      } else {
        this.resetTimer()

        this.markThreadRead()

        this.$nextTick(() => {
          this.scrollToLatestMessage()
        })
      }
    },

    subscribeThread() {
      this.isSubscribingToThread = true

      this.subscribeToThread({
        subscriptionId: `thread-${this.messageThreadId}`,
        messageThreadId: this.messageThreadId,
        callbacks: {
          onMessage: this.handleMessageEvent,
          onParticipantRemoved: this.handleParticipantRemoved,
          onSubscribe: this.handleThreadSubscription,
          onMemberAdded: this.handleMemberAdded,
          onMemberRemoved: this.handleMemberRemoved,
          onParticipantAdded: this.handleParticipantAdded
        }
      })
    },

    unsubscribeThread() {
      this.unsubscribeFromThread({
        subscriptionId: `thread-${this.messageThreadId}`,
        messageThreadId: this.messageThreadId
      })
    },

    handleMemberAdded(member) {
      this.memberIds.push(member.info.id)
      this.memberCount = this.memberCount + 1
    },

    handleMemberRemoved(member) {
      this.memberIds = this.memberIds.filter(id => id !== member.info.id)
      this.memberCount = this.memberCount - 1
    },

    handleParticipantRemoved(pusherData) {
      if (
        !this.currentUser?.isAccountAdmin &&
        pusherData.userId == this.currentUser?.id
      ) {
        this.removeMessageThread(pusherData.messageThreadId)
        this.$router.replace("/me/messages")
      }
    },

    handleParticipantAdded(pusherData) {
      this.addParticipantToThread(
        pusherData.messageThreadId,
        pusherData.participant
      )
    },

    handleThreadSubscription(e) {
      this.memberIds = Object.values(e.members).map(m => m.id)
      this.memberCount = e.count
      this.isSubscribingToThread = false
    },

    scrollToMessage(messageTs) {
      this.$nextTick(() => {
        const messageEl = document.querySelector(`[data-ts="${messageTs}"]`)

        if (this.isThread && isIos()) {
          messageEl.scrollIntoView(true)
        } else if (this.isFeed) {
          messageEl.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest"
          })
        } else {
          // Leave room for MessageCompose box
          const offset = messageEl.querySelector("[data-role=message-user]")
            ? 140
            : 120

          window.scrollTo({
            top:
              messageEl.getBoundingClientRect().top +
              (window.pageYOffset - window.innerHeight) +
              offset,
            behavior: "smooth"
          })
        }
      })
    },

    scrollToLatestMessage() {
      if (this.isCommentThread && !this.isThread) return

      // Scroll to latest message in thread if within 250px of bottom of page
      if (
        this.isThread &&
        document.documentElement.scrollHeight -
          (window.pageYOffset + window.innerHeight) <
          250
      ) {
        this.scrollToMessage(this.messageThread.latest_message.message_ts)
      } else {
        if (!this.$refs["message-thread-wrapper"]) return

        const scrollTop = this.$refs["message-thread-wrapper"].scrollTop

        // iOS Safari refuses to update scrollTop if the given value is the same as its
        // current value. That would be fine, except that the current value is _incorrect_
        // when the keyboard is open
        // This was resulting in messages hiding behind the message compose box
        // Ensuring the value is always different fixes that
        this.$refs["message-thread-wrapper"].scrollTop = isIos()
          ? scrollTop === 0
            ? -1
            : 0
          : 0
      }
    },

    scrollToCompose() {
      this.$nextTick(() => {
        const composeEl = document.querySelector("[data-role=thread-footer]")

        if (composeEl) {
          window.scrollTo({
            top:
              composeEl.getBoundingClientRect().top +
              (window.pageYOffset - window.innerHeight) +
              100,
            behavior: "smooth"
          })
        }
      })
    },

    resetTimer() {
      if (this.countdownTimer) {
        this.hideMessages = false
        this.countdownTimer.reset()
        this.countdownTimer.start()
      }
    },

    fetchThread(fromStart = true) {
      const vm = this
      this.isLoading = true

      return this.loadMessages(fromStart)
        .then(() => {
          this.markThreadRead()

          if (isPreviewContext()) {
            const { postMessage } = require("@/lib/admin-iframe-helpers")

            postMessage(WINDOW_EVENTS.UPDATE_UNREAD_STATUS, {
              hasUnread: vm.hasUnreadMessageThreads
            })
          }
        })
        .catch(() => {
          // window.location.href = "/me/messages"
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    react() {
      this.publishMessage({
        user: this.user,
        messageBody: "heart",
        messageType: MESSAGE_TYPES.REACTION
      })
    },

    setupInfiniteLoadObserver() {
      if (!this.isExpanded) return

      if (!this.$refs["load-observer"]) {
        setTimeout(() => {
          this.setupInfiniteLoadObserver()
        }, 200)
      } else if (!this.loadObserver) {
        this.loadObserver = new IntersectionObserver(entries => {
          if (entries[0].isIntersecting) {
            this.loadMessages()
          }
        })
        this.loadObserver.observe(this.$refs["load-observer"])
      }
    },

    teardownLoadObserver() {
      this.loadObserver.disconnect()
      this.loadObserver = null
    },

    setupTimerListeners() {
      if (this.overlay) {
        this.$refs["message-thread-wrapper"]?.addEventListener(
          "scroll",
          throttle(this.resetTimer, 1000)
        )

        this.$refs["message-thread-wrapper"]?.addEventListener(
          "mouseenter",
          throttle(this.resetTimer, 1000)
        )
      }
    },

    async loadMessages(fromStart = false) {
      if (!isPresent(this.messageThread)) {
        try {
          await this.fetchMessageThread(this.messageThreadId)
        } catch (e) {
          if (e.response && e.response.status === 403 && this.isAuthenticated) {
            this.$notify(
              this.$t(
                "components.messageThread.thread.notHavePermissionViewThread"
              )
            )
          }
        }
      }

      if (this.isLoadingMessages) return Promise.resolve()

      let messageToFetch = null

      if (!fromStart && isPresent(this.messages)) {
        const lastGroup = this.messages[this.messages.length - 1]

        messageToFetch =
          this.renderOrder === MESSAGE_ORDER.LATEST_FIRST
            ? lastGroup[0]?.message_ts // Comments render with the most recent comment on top
            : this.isThread
            ? lastGroup[lastGroup.length - 1]?.message_ts // Threads render with the most recent message at the bottom
            : this.messages[0][0]?.message_ts // Chats render with the oldest message at the top
      }

      if (
        !fromStart &&
        isPresent(this.messages) &&
        this.lastMessageFetched === messageToFetch
      )
        return Promise.resolve()

      this.isLoadingMessages = true
      this.lastMessageFetched = messageToFetch

      let args = {
        messageThreadId: this.messageThreadId,
        order: this.messageOrder
      }

      if (messageToFetch) {
        args = {
          ...args,
          [this.messageOrder === MESSAGE_ORDER.LATEST_FIRST
            ? "before"
            : "after"]: messageToFetch
        }
      }

      return this.fetchMessages(args)
        .then(() => {
          this.isLoadingMessages = false

          // Load entire thread so that we can confidently scroll
          // to bottom if user messages in the thread
          if (this.isThread && this.messageThread.remaining_messages !== 0) {
            this.loadMessages()
          }
        })
        .finally(() => {
          this.isLoadingMessages = false
        })
    },

    username(user) {
      return user.name ? user.name : user.email.split("@")[0]
    },

    initializeMessageThread() {
      if (this.isInitialized || !this.hasCheckedAuthentication) return

      let hasSubscribed = false

      if (isPresent(this.messageThread)) {
        this.subscribeThread()
        this.isSubscribingToThread = false
        hasSubscribed = true
      }

      this.fetchThread().then(() => {
        if (this.isAuthenticated) {
          if (!hasSubscribed) this.subscribeThread()

          if (this.overlay && this.autoHide) {
            this.countdownTimer = new Timer({
              countDirection: COUNT_DIRECTIONS.DOWN,
              seconds: 5
            })
            this.countdownTimer.on(TIMER_EVENTS.FINISHED, () => {
              this.hideMessages = true
            })
            this.countdownTimer.start()
          }

          this.$nextTick(() => {
            this.setupInfiniteLoadObserver()
            this.setupTimerListeners()
          })

          this.isInitialized = true
        }
      })
    },

    expandThread() {
      const scrollToNew = !this.isCommentThread
      this.isExpanded = true

      this.$nextTick(() => {
        this.setupInfiniteLoadObserver()
        if (scrollToNew) this.scrollToLatestMessage()
      })

      this.$emit("expand")
    },

    collapseThread() {
      this.isExpanded = false
      this.teardownLoadObserver()
      this.$emit("collapse")
    },

    handleComposeFocus() {
      if (!this.isCommentThread) {
        this.$refs["message-thread"].scrollIntoView({ behavior: "smooth" })
      }
    },

    handleComposeBlur() {
      if (isIos()) {
        // Triggers iOS to re-layout the page and prevent large bars at
        // the bottom of the screen
        // Wait 400ms for the virtual keyboard to hide
        setTimeout(() => {
          window.scrollBy({
            top: -1,
            behavior: "smooth"
          })
        }, 400)
      }
    }
  },

  deactivated() {
    this.unsubscribeThread()
  },

  beforeDestroy() {
    this.unsubscribeThread()
  },

  activated() {
    this.fetchThread(true)
  },

  watch: {
    isAuthenticated() {
      if (this.isAuthenticated) {
        this.initializeMessageThread()
        this.fetchMessageThread(this.messageThreadId) // This ensures the currentParticipant is updated
      }
    }
  },

  mounted() {
    this.initializeMessageThread()
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

$message-thread--top-padding: 48px;
$message-thread--footer-height: 60px;

$thread--left-padding: 34px;

.message-thread {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 300px;

  &.--comments,
  &.--feed {
    .message-thread__content-wrapper {
      mask-image: none;
    }

    .message-thread__content-container {
      flex-direction: column;
    }

    .message-thread__content {
      padding-top: $size--2;
    }
  }

  &.--comments {
    &:not(.--thread) {
      flex-direction: column-reverse;

      .message-thread__footer-wrapper {
        padding-top: $size--4;
        margin-bottom: 0;
      }
    }
  }

  &.--feed {
    .message-thread__content {
      padding: 0;

      user-select: auto;
    }

    .message-thread__group .message:not(.--is-in-group) {
      padding: $size--3 $size--3 $size--2;
      border: 1px solid $color-ui--grey-20;
      background: $color-ui--white;
      margin-bottom: $size--8;
      border-radius: var(--border-radius);
    }

    .feed__footer-wrapper {
      position: sticky;
      bottom: calc(#{$tabbar--height} + 1rem);
      padding-bottom: env(safe-area-inset-bottom);
      align-self: flex-end;
      margin-right: 0.5rem;
      z-index: 10;
    }
  }

  &.--thread {
    .message-thread__footer-wrapper {
      padding-left: $thread--left-padding;
    }

    .message-thread__content-wrapper {
      padding-left: $thread--left-padding + 2px;
      flex-direction: column;
      mask-image: none;
    }

    .message-thread__content {
      padding-top: 0rem;
    }

    .thread__content-user.--is-self {
      text-align: left;
    }

    .subscriber-avatar {
      border: 2px solid $color-ui--grey-10;
    }
  }

  &.--overlay {
    .message-compose__text {
      background: transparent;
    }

    .message__wrapper {
      .message__body {
        .body-text {
          color: $color-ui--white;
          text-shadow: 0 1px 1px $color-ui--black;
          background-color: rgba($color-ui--grey-30, 0.24);
          backdrop-filter: blur(2px);
        }
      }

      &.--is-self {
        flex-direction: row;

        .message__body {
          .body-text {
            color: $color-ui--white;
            margin-right: 0.5rem;
            border-top-left-radius: 4px;
            border-bottom-left-radius: 4px;
            border-top-right-radius: 16px;
            border-bottom-right-radius: 16px;
          }
        }
      }

      &.--is-last-by-user {
        .message__body {
          .body-text {
            border-bottom-left-radius: 16px;
            border-bottom-right-radius: 16px;
          }
        }
      }

      &.--is-first-by-user {
        .message__body {
          .body-text {
            border-top-left-radius: 16px;
            border-top-right-radius: 16px;
          }
        }
      }
    }

    .message__user {
      color: $color-ui--white;
      text-shadow: 0 1px 1px $color-ui--black;

      &.--is-self {
        text-align: left;
      }
    }

    .timestamp-ago {
      color: $color-ui--grey-70;

      &.--is-self {
        text-align: left;
      }
    }

    .message-thread__notification {
      text-align: left;
      margin-left: $size--9;
      color: $color-ui--white;
      text-shadow: 0 1px 2px $color-ui--black;
    }

    .overlay-pill {
      position: absolute;
      top: -2.5rem;
      right: 1.2rem;
    }
  }
}

.message-thread__content {
  flex-grow: 1;
  padding: $message-thread--top-padding $size--4 $size--4 $size--4;
  user-select: none;
}

.message-thread__content-wrapper {
  @include hide-scroll-bars;

  position: relative; // Required to trigger offsetParent
  overflow-x: none;
  overflow-y: scroll;
  flex-grow: 1;
  display: flex;
  flex-direction: column-reverse;
  opacity: 1;
  transition: opacity 0.6s ease;
  mask-image: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0) 0%,
    rgb(255, 255, 255) 16%
  );

  .message-thread__content-container {
    display: flex;
    flex-direction: column-reverse;
    width: 100%;
  }
}

.thread__line-vert {
  position: absolute;
  width: 2px;
  height: calc(100% - 3rem);
  left: -4px;
  top: 0px;
  background: $color-ui--grey-20;
}

@media screen and (max-width: $breakpoint--md) {
  .message-thread {
    &.--feed {
      .message-thread__group {
        border-left: none;
        border-right: none;
        border-radius: 0;
      }
    }
  }
}
</style>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mk-lightbox";

.message-thread__footer-wrapper {
  position: relative;
  z-index: 20;
  margin-bottom: $size--5;
  margin-bottom: calc(#{$size--5} + env(safe-area-inset-bottom));
  flex-direction: column;
}

.message-thread {
  .message-thread__footer-wrapper {
    position: sticky;
    bottom: $size--5;
    bottom: calc(#{$size--4} + env(safe-area-inset-bottom));
  }

  .--comments:not(.--thread) {
    .message-thread__footer-wrapper {
      position: relative;
      bottom: initial;
    }
  }

  &.--overlay {
    .message-thread__footer-wrapper {
      flex-direction: row;
    }
  }
}

.message-thread__footer-action {
  position: sticky;
  z-index: 10;
  bottom: 1rem;
  top: $toolbar--height;
  top: calc(#{$toolbar--height} + env(safe-area-inset-top));
  height: 0px;
  width: 100%;
  text-align: center;

  .v-button {
    position: relative;
    top: 16px;
    background: $color-ui--white;

    &:focus,
    &:active {
      background: $color-ui--white;
    }
  }
}

.message-thread.--hide-messages {
  .message-thread__content-wrapper {
    opacity: 0;
  }
}

.message-thread__context {
  .v-button {
    background: white;

    .v-icon {
      color: #ff2e2b;
      transform: scale(1.3);
    }
  }
}

.green-dot {
  height: 0.5rem;
  width: 0.5rem;
  background-color: $color-ui--green-dot;
  border-radius: 50%;
  display: inline-block;
  animation: fade-in-out 2s ease-in-out infinite;
  position: relative;
  bottom: 1px;
}
</style>
